import { useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Image, Upload, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png";

export const LogoSection = ({ subscription, companyLogo, updateLogo }) => {
  const fileInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large. Max 2MB.");
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateLogo(res.data.logo_url);
      toast.success("Company logo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleResetLogo = async () => {
    try {
      await axios.put(`${API}/admin/company-settings`, { logo_url: "" }, { withCredentials: true });
      updateLogo("");
      toast.success("Logo reset to default");
    } catch {
      toast.error("Failed to reset logo");
    }
  };

  const isGated = !subscription?.plan_id || subscription.plan_id === "starter" || subscription.plan_id === "pro";

  return (
    <Card className="card-surface" data-testid="logo-card">
      <CardHeader style={{ borderBottom: '1px solid var(--t-border)', padding: '16px 20px' }}>
        <div className="flex items-center gap-3">
          <Image className="w-5 h-5" style={{ color: 'var(--t-text-muted)' }} />
          <div>
            <CardTitle className="text-lg font-semibold" style={{ color: 'var(--t-text)', fontFamily: 'var(--t-heading-font)' }}>
              Company Logo
            </CardTitle>
            <CardDescription style={{ color: 'var(--t-text-muted)' }}>Customize the logo shown in the sidebar</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {isGated ? (
          <div className="flex items-center gap-6" data-testid="logo-plan-gate">
            <div className="flex-shrink-0 w-32 h-16 rounded-xl flex items-center justify-center p-2 bg-gray-900">
              <img src={DEFAULT_LOGO} alt="EasyOffice Logo" className="h-8 w-auto" style={{ maxWidth: '120px' }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>EasyOffice Branding</p>
              <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
                Custom logo upload is available on the <strong>Team</strong> plan and above.
              </p>
              <Button
                size="sm"
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 px-3"
                onClick={() => document.getElementById("subscription-section")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="upgrade-logo-btn"
              >
                Upgrade for Custom Logo
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-6">
            <div className="flex-shrink-0 w-32 h-16 rounded-xl flex items-center justify-center p-2" style={{ background: 'var(--t-sidebar)' }}>
              <img src={companyLogo || DEFAULT_LOGO} alt="Current Logo" className="h-8 w-auto" style={{ maxWidth: '120px' }} />
            </div>
            <div className="flex-1 space-y-3">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              <div className="flex gap-2">
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo} className="btn-primary" data-testid="upload-logo-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? "Uploading..." : "Upload Logo"}
                </Button>
                {companyLogo && (
                  <Button variant="outline" onClick={handleResetLogo} data-testid="reset-logo-btn" style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                )}
              </div>
              <p className="text-xs" style={{ color: 'var(--t-text-subtle)' }}>PNG, JPG, or SVG. Max 2MB. Recommended: transparent background.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
